/**
 * Drawing MegaMek's art while it is still arriving.
 *
 * The sprites are fetched now rather than bundled (`megamek.ts` says why), so
 * on a first visit there is a moment between a hex existing and a machine
 * standing in it. Cached immutably for a year afterwards, so this is the first
 * visit's problem only — but the first visit is the one worth getting right,
 * and a hex that fills in later reads as breakage rather than as loading.
 *
 * Two things live here, and both are shared so that every screen agrees:
 *
 * - one decode cache, so a sprite drawn on the daily card and again in the
 *   lobby is fetched once for the life of the page rather than once per
 *   screen;
 * - one placeholder, so a machine that has not arrived looks the same
 *   wherever it is waiting.
 *
 * The placeholder is drawn here rather than fetched. Anything fetched to stand
 * in for something being fetched is the same problem again, one round trip
 * later.
 */

import { SPRITE_H, SPRITE_W, loadTile } from "./camo/mech";

/** Decoded tiles, once each for the life of the page. */
const tiles = new Map<string, Promise<ImageData>>();

/**
 * One decoded sprite, shared across screens.
 *
 * A failed load is dropped from the cache rather than kept: a transient miss —
 * a dev-server hiccup, a briefly starved network — must not poison a URL for
 * the life of the page, and the next redraw simply tries again.
 */
export function tile(url: string): Promise<ImageData> {
  let loading = tiles.get(url);
  if (!loading) {
    loading = loadTile(url).catch((error: unknown) => {
      tiles.delete(url);
      throw error;
    });
    tiles.set(url, loading);
  }
  return loading;
}

/** Whether a sprite is already decoded, so a caller can skip the placeholder
 * entirely on a revisit rather than flashing one for a frame. */
export function isLoaded(url: string): boolean {
  return tiles.has(url);
}

let ghostData: ImageData | null = null;

/**
 * The mark that stands where a machine is about to.
 *
 * A plinth rather than a silhouette of anything: it has to sit under every
 * unit type in the library — a Mek, a tank, a dropship — without implying
 * which one is coming, and without being mistaken for art that failed to
 * paint. Built once and reused, because it is drawn per hex.
 */
export function ghost(): ImageData {
  if (ghostData) return ghostData;

  const canvas = document.createElement("canvas");
  canvas.width = SPRITE_W;
  canvas.height = SPRITE_H;
  const ctx = canvas.getContext("2d")!;

  // Roughly the footprint a Mek sprite occupies in its tile, centred on the
  // hex rather than on the canvas: the art sits slightly high, and a mark
  // that does not sit where the machine will lands as a second thing on the
  // board instead of a promise about the first.
  const w = 30;
  const h = 34;
  const x = (SPRITE_W - w) / 2;
  const y = (SPRITE_H - h) / 2 - 3;

  ctx.fillStyle = "rgba(150, 172, 205, 0.14)";
  ctx.strokeStyle = "rgba(150, 172, 205, 0.28)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(x + 0.5, y + 0.5, w, h, 5);
  ctx.fill();
  ctx.stroke();

  ghostData = ctx.getImageData(0, 0, SPRITE_W, SPRITE_H);
  return ghostData;
}

/**
 * Paint a unit's canvas: the placeholder now, the art when it arrives.
 *
 * `data-state` carries which of those is on screen, and styles.css does the
 * rest — a slow pulse while waiting and a short fade on arrival, both of which
 * stop under `prefers-reduced-motion`. The state is on the element rather than
 * in the pixels because `putImageData` ignores `globalAlpha`, so a fade has to
 * be CSS whatever else happens.
 *
 * `stale` lets a screen abandon a paint it has already replaced; the scenario
 * preview redraws on every scenario change and a late sprite must not land on
 * a newer board.
 */
export function paintWhenReady(
  canvas: HTMLCanvasElement,
  load: () => Promise<ImageData>,
  stale: () => boolean = () => false,
): Promise<void> {
  const ctx = canvas.getContext("2d")!;
  canvas.dataset.state = "loading";
  ctx.putImageData(ghost(), 0, 0);

  return load().then(
    (art) => {
      if (stale()) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.putImageData(art, 0, 0);
      canvas.dataset.state = "ready";
    },
    (error: unknown) => {
      if (stale()) return;
      // The placeholder stays. A hex that cannot draw keeps its mark rather
      // than emptying out, and the caption still names the unit.
      canvas.dataset.state = "missing";
      throw error;
    },
  );
}
